import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { BehaviorSubject, catchError, filter, map, mergeMap, Observable, of, switchMap, take, throwError } from 'rxjs';

import { EcmDispatcherModule } from '@ecm/dispatcher';

import { Contract } from '../business/api/contracts/contract/contract';
import { ContractsBusinessModel } from '../business/api/contracts/contracts.business-model';
import { Brand } from '../business/context/brand.enum';
import { UrlHelper } from '../utils/url-helper';
import { PortfolioService } from './portfolio.service';

@Injectable({
    providedIn: 'root',
})
export class ContextService {
    /**
     * Class name for logger
     * @type {string}
     */
    private static CLASS_NAME = 'ContextService';

    private currentBrand: Brand;

    private readonly _contextId$ = new BehaviorSubject<string>('');
    // public ctxId = toSignal(this.contextId$);
    // Contract ID regex
    public static CIDRegexp = /^[0-9]{10}$/; // restricting the CID match to 10 numbers only
    // Member ID regex
    public static MIDRegexp = /^[1-9][0-9]{17}[0-9]?$/; // restricting the MID match to 18 numbers only

    public constructor(
        @Inject(PLATFORM_ID) protected platformId: any,
        private activatedRoute: ActivatedRoute,
        private portfolioService: PortfolioService,
        private router: Router,
    ) {}

    public get contextId$(): Observable<string> {
        return this._contextId$.asObservable();
    }

    public updateContextId(contextId: string): void {
        this._contextId$.next(contextId);
    }

    /**
     * Is user authenticated
     * @returns {Observable<boolean>}
     */
    public isAuthenticated(): Observable<boolean> {
        if (isPlatformBrowser(this.platformId)) {
            const IDENT_LEVEL_KEY = 'USER_IDENT_LEVEL';
            if ((<any>window).o_idzone && (<any>window).o_idzone[IDENT_LEVEL_KEY]) {
                const userIdentLevel: string = (<any>window).o_idzone[IDENT_LEVEL_KEY];
                return of(userIdentLevel.toUpperCase() === 'FULL');
            } else {
                return this.portfolioService.getLoggedContract().pipe(
                    map(() => true),
                    catchError(() => of(false)),
                );
            }
        } else {
            return of(false);
        }
    }

    /**
     * Is current brand Sosh
     * @returns {Observable<boolean>}
     */
    public isSosh(): Observable<boolean> {
        return this.getBrand().pipe(
            take(1),
            map((brand: Brand) => {
                this.currentBrand = brand;
                const isSosh: boolean = brand === Brand.SOSH;
                EcmDispatcherModule.sendConsoleInfo(ContextService.CLASS_NAME, 'User brand is ' + brand);
                return isSosh;
            }),
        );
    }

    private checkUserPortfolioBrandsAndType(contractsBusinessModel: ContractsBusinessModel) {
        let hasSosh = false;
        let hasOrange = false;
        let hasVimEligible = false;
        contractsBusinessModel.contracts.forEach((contract: Contract) => {
            const brand: string = contract.brand.toUpperCase();
            if (brand === Brand.SOSH) {
                hasSosh = true;
            } else {
                hasOrange = true;
            }
            if (contract.telco?.state?.toUpperCase() === 'VIM_ELIGIBLE') {
                hasVimEligible = true;
            }
        });
        return {
            hasVimEligible,
            isFullSosh: hasSosh && !hasOrange,
            isFullOrange: hasOrange && !hasSosh,
        };
    }

    /**
     * Brand calculator
     * based on user Portfolio and current route
     * @returns {Observable<Brand>}
     */
    private getBrand(): Observable<Brand> {
        if (!isPlatformBrowser(this.platformId)) {
            return of(Brand.ORANGE);
        }
        return this.portfolioService.getRawContracts().pipe(
            filter(() => {
                return this.router.routerState.snapshot.url !== '';
            }),
            mergeMap((contractsBusinessModel: ContractsBusinessModel) => {
                const { hasVimEligible, isFullSosh, isFullOrange } =
                    this.checkUserPortfolioBrandsAndType(contractsBusinessModel);

                // top-level cases
                if (isFullSosh) {
                    EcmDispatcherModule.sendConsoleDebug(ContextService.CLASS_NAME, 'User is full Sosh');
                    return of(Brand.SOSH);
                } else if (isFullOrange) {
                    EcmDispatcherModule.sendConsoleDebug(ContextService.CLASS_NAME, 'User is full Orange');
                    return of(Brand.ORANGE);
                } else if (hasVimEligible) {
                    // if user has at least 1 `VIM_ELIGIBLE` contract, always return logged contract brand
                    return this.portfolioService.getLoggedContract().pipe(
                        map((loggedContract: Contract) => {
                            return loggedContract.brand.toUpperCase() === Brand.SOSH ? Brand.SOSH : Brand.ORANGE;
                        }),
                        catchError((e) => {
                            return throwError(() => e);
                        }),
                    );
                } else {
                    return this.handleMixedBrandsCase();
                }
            }),
            catchError(() => {
                // in case of error, return brand Orange by default
                EcmDispatcherModule.sendConsoleDebug(
                    ContextService.CLASS_NAME,
                    'Error on portfolio request (/contracts)',
                );
                return of(Brand.ORANGE);
            }),
        );
    }

    private handleMixedBrandsCase() {
        // mixed brands case
        EcmDispatcherModule.sendConsoleDebug(ContextService.CLASS_NAME, 'User is mixed Orange / Sosh');
        // check if cid present in current route
        const href: string = globalThis.location.href;
        const stateUrl: string = this.router.routerState.snapshot.url;
        const cidMatch: string[] = UrlHelper.matchCidInPath(stateUrl);
        const midMatch: string[] = UrlHelper.matchMidInPath(stateUrl);
        // Regex to match "carrousel" query params value in URL
        const matchCarrouselQueryParamsValue = /[?&]carrousel=([^&]+)/;

        // Get carrousel query params value in url
        const carrouselValueInUrl = stateUrl.match(matchCarrouselQueryParamsValue);

        if (cidMatch) {
            const cid = cidMatch[1];
            return this.handleMixedBrandsCaseWithMatchedCid(cid);
        } else if (midMatch) {
            const mid = midMatch[1];
            return this.handleMixedBrandsCaseWithMatchedMid(mid);
        } else if (carrouselValueInUrl) {
            const encodedCidOrMid = carrouselValueInUrl[1];
            // decode URL encoded cid
            const urlDecoded = decodeURIComponent(encodedCidOrMid);
            const decodedCidOrMid = atob(urlDecoded);
            if (decodedCidOrMid && decodedCidOrMid.match(ContextService.CIDRegexp)) {
                return this.handleMixedBrandsCaseWithMatchedCid(decodedCidOrMid);
            } else if (decodedCidOrMid && decodedCidOrMid.match(ContextService.MIDRegexp)) {
                return this.handleMixedBrandsCaseWithMatchedMid(decodedCidOrMid);
            }
        }
        // current route has no cid (transversal route)
        EcmDispatcherModule.sendConsoleDebug(ContextService.CLASS_NAME, 'Transversal route');
        // check `sosh` or `orange` query param presence
        EcmDispatcherModule.sendConsoleDebug(ContextService.CLASS_NAME, 'Will check query params');

        return this.activatedRoute.queryParams.pipe(
            filter((params: Params) => {
                /**
                 * FGO : Hacky test to filter Angular Router initialization
                 *   with empty queryParams (even if query params set in URL...)
                 *   See https://github.com/angular/angular/issues/12157
                 */
                return Object.keys(params).length > 0 === (href.indexOf('?') !== -1 || stateUrl.indexOf('?') !== -1);
            }),
            map((params: Params) => {
                const lowerCaseParams: Params = {};
                for (const key of Object.keys(params)) {
                    lowerCaseParams[key.toLowerCase()] = params[key];
                }
                return lowerCaseParams;
            }),
            map((params: Params) => {
                if (Object.prototype.hasOwnProperty.call(params, 'sosh')) {
                    return Brand.SOSH;
                } else if (Object.prototype.hasOwnProperty.call(params, 'orange')) {
                    return Brand.ORANGE;
                } else if (!this.currentBrand) {
                    // default case : brand Orange
                    EcmDispatcherModule.sendConsoleDebug(ContextService.CLASS_NAME, 'No query param for brand');
                    return Brand.ORANGE;
                }
                // no query param and brand is already set, just keep using it
                EcmDispatcherModule.sendConsoleDebug(
                    ContextService.CLASS_NAME,
                    'No query param and brand is already set : ' + this.currentBrand + ' => Will stay on this brand',
                );
                return this.currentBrand;
            }),
        );
    }

    private handleMixedBrandsCaseWithMatchedCid(cid: string) {
        EcmDispatcherModule.sendConsoleDebug(
            ContextService.CLASS_NAME,
            'Contract route, will handle brand by cid (ContractId)',
        );
        // current route has a cid, set brand according to cid
        return this.portfolioService.getContractByCid(cid).pipe(
            map((contract: Contract) => {
                // cid belongs to portfolio
                if (contract?.brand?.toLowerCase() === Brand.SOSH.toLowerCase()) {
                    return Brand.SOSH;
                }
                // cid doesn't belong to portfolio
                return Brand.ORANGE;
            }),
            catchError((e) => {
                return throwError(() => e);
            }),
        );
    }

    private handleMixedBrandsCaseWithMatchedMid(mid: string) {
        EcmDispatcherModule.sendConsoleDebug(
            ContextService.CLASS_NAME,
            'Contract route, will handle brand by mid (MemberId)',
        );
        return this.portfolioService.getRawContracts().pipe(
            switchMap((contract: ContractsBusinessModel) => {
                const currentCid: any = contract?.contracts?.find((contract: Contract) => {
                    return contract?.associatedFamilyMembers?.some((member) => member.id === mid);
                }).cid;
                return this.handleMixedBrandsCaseWithMatchedCid(currentCid);
            }),
        );
    }
}
